import React, { useEffect, useState } from 'react';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import apiService from '../services/api';
import {
  defaultEmbeddingDocsDocument,
  normalizeEmbeddingDocsDocument,
} from '../data/embeddingDocsDefaults';
import '../styles/StaffPages.css';

function createTopic() {
  return {
    slug: `topic-${Date.now()}`,
    label: 'New Topic',
    title: 'New Topic Title',
    intro: '',
    subtopics: [],
  };
}

function createSubtopic() {
  return {
    id: `subtopic-${Date.now()}`,
    label: 'New Subtopic',
    blocks: [],
  };
}

function createBlock(type = 'paragraph') {
  if (type === 'code') {
    return { type: 'code', label: 'Code Example', code: '' };
  }
  if (type === 'callout') {
    return { type: 'callout', title: 'Callout', text: '' };
  }
  if (type === 'list') {
    return { type: 'list', items: [''] };
  }
  if (type === 'table') {
    return { type: 'table', columns: ['Column 1', 'Column 2'], rows: [['', '']] };
  }
  return { type: 'paragraph', text: '' };
}

function moveItem(items, fromIndex, direction) {
  const nextIndex = fromIndex + direction;
  if (nextIndex < 0 || nextIndex >= items.length) {
    return items;
  }
  const updated = [...items];
  const [item] = updated.splice(fromIndex, 1);
  updated.splice(nextIndex, 0, item);
  return updated;
}

function listToTextarea(items = []) {
  return items.join('\n');
}

function textareaToList(value) {
  return value.split('\n');
}

function tableColumnsToTextarea(columns = []) {
  return columns.join('\n');
}

function textareaToTableColumns(value) {
  return value.split('\n').filter(Boolean);
}

function tableRowsToTextarea(rows = []) {
  return rows.map((row) => row.join(' | ')).join('\n');
}

function textareaToTableRows(value) {
  return value
    .split('\n')
    .filter(Boolean)
    .map((row) => row.split('|').map((cell) => cell.trim()));
}

function buildCollapsedTopicsState(documentData) {
  const state = {};
  (documentData?.topics || []).forEach((topic, topicIndex) => {
    state[`${topic.slug || 'topic'}-${topicIndex}`] = true;
  });
  return state;
}

function buildCollapsedSubtopicsState(documentData) {
  const state = {};
  (documentData?.topics || []).forEach((topic, topicIndex) => {
    const topicKey = `${topic.slug || 'topic'}-${topicIndex}`;
    (topic.subtopics || []).forEach((subtopic, subtopicIndex) => {
      state[`${topicKey}-${subtopic.id || 'subtopic'}-${subtopicIndex}`] = true;
    });
  });
  return state;
}

const compactActionButtonStyle = {
  width: '92px',
  height: '28px',
  padding: '0 8px',
  gap: '6px',
  minHeight: '28px',
  borderRadius: '999px',
  justifyContent: 'center',
  display: 'inline-flex',
  alignItems: 'center',
};

const compactActionButtonTextStyle = {
  fontSize: '11px',
  lineHeight: '100%',
  textAlign: 'center',
  whiteSpace: 'nowrap',
};

function BlockEditor({ block, onChange, onMoveUp, onMoveDown, onRemove }) {
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px', marginTop: '12px', background: '#fff' }}>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
        <select
          className="form-select"
          value={block.type}
          onChange={(e) => onChange(createBlock(e.target.value))}
          style={{ maxWidth: '220px' }}
        >
          <option value="paragraph">Paragraph</option>
          <option value="code">Code</option>
          <option value="callout">Callout</option>
          <option value="list">List</option>
          <option value="table">Table</option>
        </select>
        <button type="button" className="staff-page-button" onClick={onMoveUp} style={compactActionButtonStyle}>
          <span className="staff-page-button-text" style={compactActionButtonTextStyle}>Up</span>
        </button>
        <button type="button" className="staff-page-button" onClick={onMoveDown} style={compactActionButtonStyle}>
          <span className="staff-page-button-text" style={compactActionButtonTextStyle}>Down</span>
        </button>
        <button type="button" className="staff-page-button" onClick={onRemove} style={{ ...compactActionButtonStyle, background: '#ffd7d7' }}>
          <span className="staff-page-button-text" style={compactActionButtonTextStyle}>Remove Block</span>
        </button>
      </div>

      {block.type === 'paragraph' && (
        <textarea
          className="form-textarea"
          rows="5"
          value={block.text || ''}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
          placeholder="Paragraph text"
        />
      )}

      {block.type === 'code' && (
        <>
          <input
            className="form-input"
            value={block.label || ''}
            onChange={(e) => onChange({ ...block, label: e.target.value })}
            placeholder="Code block label"
            style={{ marginBottom: '12px' }}
          />
          <textarea
            className="form-textarea"
            rows="10"
            value={block.code || ''}
            onChange={(e) => onChange({ ...block, code: e.target.value })}
            placeholder="Code content"
          />
        </>
      )}

      {block.type === 'callout' && (
        <>
          <input
            className="form-input"
            value={block.title || ''}
            onChange={(e) => onChange({ ...block, title: e.target.value })}
            placeholder="Callout title"
            style={{ marginBottom: '12px' }}
          />
          <textarea
            className="form-textarea"
            rows="4"
            value={block.text || ''}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            placeholder="Callout text"
          />
        </>
      )}

      {block.type === 'list' && (
        <textarea
          className="form-textarea"
          rows="6"
          value={listToTextarea(block.items)}
          onChange={(e) => onChange({ ...block, items: textareaToList(e.target.value) })}
          placeholder="One list item per line"
        />
      )}

      {block.type === 'table' && (
        <>
          <textarea
            className="form-textarea"
            rows="4"
            value={tableColumnsToTextarea(block.columns)}
            onChange={(e) => onChange({ ...block, columns: textareaToTableColumns(e.target.value) })}
            placeholder="One column per line"
            style={{ marginBottom: '12px' }}
          />
          <textarea
            className="form-textarea"
            rows="6"
            value={tableRowsToTextarea(block.rows)}
            onChange={(e) => onChange({ ...block, rows: textareaToTableRows(e.target.value) })}
            placeholder="One row per line, separate cells with |"
          />
        </>
      )}
    </div>
  );
}

const SuperuserDocsManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [documentData, setDocumentData] = useState(() => normalizeEmbeddingDocsDocument(defaultEmbeddingDocsDocument));
  const [collapsedTopics, setCollapsedTopics] = useState({});
  const [collapsedSubtopics, setCollapsedSubtopics] = useState({});

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          window.location.href = '/staff/dashboard/';
          return;
        }

        const response = await apiService.getEmbeddingDocsSuperuser();
        if (response?.document?.topics?.length) {
          const normalizedDocument = normalizeEmbeddingDocsDocument(response.document);
          setDocumentData(normalizedDocument);
          setCollapsedTopics(buildCollapsedTopicsState(normalizedDocument));
          setCollapsedSubtopics(buildCollapsedSubtopicsState(normalizedDocument));
        } else {
          const fallbackDocument = normalizeEmbeddingDocsDocument(defaultEmbeddingDocsDocument);
          setDocumentData(fallbackDocument);
          setCollapsedTopics(buildCollapsedTopicsState(fallbackDocument));
          setCollapsedSubtopics(buildCollapsedSubtopicsState(fallbackDocument));
        }
      } catch (error) {
        console.error('Failed to load docs config:', error);
        showToast('Failed to load docs config', 'error');
        const fallbackDocument = normalizeEmbeddingDocsDocument(defaultEmbeddingDocsDocument);
        setDocumentData(fallbackDocument);
        setCollapsedTopics(buildCollapsedTopicsState(fallbackDocument));
        setCollapsedSubtopics(buildCollapsedSubtopicsState(fallbackDocument));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const updateTopic = (topicIndex, nextTopic) => {
    setDocumentData((prev) => ({
      ...prev,
      topics: prev.topics.map((topic, index) => (index === topicIndex ? nextTopic : topic)),
    }));
  };

  const updateSubtopic = (topicIndex, subtopicIndex, nextSubtopic) => {
    const topic = documentData.topics[topicIndex];
    updateTopic(topicIndex, {
      ...topic,
      subtopics: topic.subtopics.map((subtopic, index) => (index === subtopicIndex ? nextSubtopic : subtopic)),
    });
  };

  const updateBlock = (topicIndex, subtopicIndex, blockIndex, nextBlock) => {
    const subtopic = documentData.topics[topicIndex].subtopics[subtopicIndex];
    updateSubtopic(topicIndex, subtopicIndex, {
      ...subtopic,
      blocks: subtopic.blocks.map((block, index) => (index === blockIndex ? nextBlock : block)),
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const normalizedDocument = normalizeEmbeddingDocsDocument(documentData);
      const response = await apiService.updateEmbeddingDocsSuperuser(normalizedDocument);
      if (response?.document) {
        setDocumentData(normalizeEmbeddingDocsDocument(response.document));
      }
      showToast('Docs updated');
    } catch (error) {
      console.error('Failed to save docs config:', error);
      showToast(error?.response?.data?.error || 'Failed to save docs config', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleTopicCollapsed = (topicKey) => {
    setCollapsedTopics((prev) => ({
      ...prev,
      [topicKey]: !prev[topicKey],
    }));
  };

  const toggleSubtopicCollapsed = (subtopicKey) => {
    setCollapsedSubtopics((prev) => ({
      ...prev,
      [subtopicKey]: !prev[subtopicKey],
    }));
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />
          <div className="staff-page-header-row">
            <div>
              <h1 className="staff-page-header">Docs</h1>
              <p className="staff-page-subheader">Edit public embedding documentation topics and subtopics</p>
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="staff-page-button"
                onClick={() => {
                  const fallbackDocument = normalizeEmbeddingDocsDocument(defaultEmbeddingDocsDocument);
                  setDocumentData(fallbackDocument);
                  setCollapsedTopics(buildCollapsedTopicsState(fallbackDocument));
                  setCollapsedSubtopics(buildCollapsedSubtopicsState(fallbackDocument));
                }}
                style={{ background: '#e6f4ff' }}
              >
                <span className="staff-page-button-text">Reset to Default</span>
              </button>
              <button
                type="button"
                className="staff-page-button"
                onClick={() => window.open('/docs/embedding/', '_blank')}
                style={{ background: '#dff8e7' }}
              >
                <span className="staff-page-button-text">Open Docs</span>
              </button>
              <button type="button" className="staff-page-button" onClick={handleSave} disabled={saving}>
                <span className="staff-page-button-text">{saving ? 'Saving...' : 'Save Docs'}</span>
              </button>
            </div>
          </div>

          {toast && <div className={`toast toast-${toast.type || 'success'}`}>{toast.message}</div>}

          <div className="add-reward-section" style={{ marginTop: '24px' }}>
            <h2 className="staff-section-header">Sidebar</h2>
            <div className="form-group">
              <label className="form-label">Eyebrow</label>
              <input
                className="form-input"
                value={documentData.sidebarEyebrow || ''}
                onChange={(e) => setDocumentData((prev) => ({ ...prev, sidebarEyebrow: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Sidebar title</label>
              <input
                className="form-input"
                value={documentData.sidebarTitle || ''}
                onChange={(e) => setDocumentData((prev) => ({ ...prev, sidebarTitle: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Sidebar copy</label>
              <textarea
                className="form-textarea"
                rows="3"
                value={documentData.sidebarCopy || ''}
                onChange={(e) => setDocumentData((prev) => ({ ...prev, sidebarCopy: e.target.value }))}
              />
            </div>
          </div>

          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="staff-section-header" style={{ marginBottom: 0 }}>Topics</h2>
            <button
              type="button"
              className="staff-page-button"
              onClick={() => setDocumentData((prev) => ({ ...prev, topics: [...prev.topics, createTopic()] }))}
            >
              <span className="staff-page-button-text">+ Add Topic</span>
            </button>
          </div>

          {documentData.topics.map((topic, topicIndex) => (
            <div key={`${topic.slug}-${topicIndex}`} className="add-reward-section" style={{ marginTop: '20px' }}>
              {(() => {
                const topicKey = `${topic.slug || 'topic'}-${topicIndex}`;
                const isTopicCollapsed = Boolean(collapsedTopics[topicKey]);

                return (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                      <div>
                        <h2 className="staff-section-header" style={{ marginBottom: '6px' }}>
                          Topic {topicIndex + 1}: {topic.label || 'Untitled'}
                        </h2>
                        <p className="staff-page-subheader">Public route: /docs/embedding/{topic.slug || 'topic'}/</p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="staff-page-button"
                          onClick={() => toggleTopicCollapsed(topicKey)}
                          style={{ ...compactActionButtonStyle, background: '#eef2ff' }}
                        >
                          <span className="staff-page-button-text" style={compactActionButtonTextStyle}>{isTopicCollapsed ? 'Expand' : 'Collapse'}</span>
                        </button>
                        <button type="button" className="staff-page-button" onClick={() => setDocumentData((prev) => ({ ...prev, topics: moveItem(prev.topics, topicIndex, -1) }))} style={compactActionButtonStyle}>
                          <span className="staff-page-button-text" style={compactActionButtonTextStyle}>Up</span>
                        </button>
                        <button type="button" className="staff-page-button" onClick={() => setDocumentData((prev) => ({ ...prev, topics: moveItem(prev.topics, topicIndex, 1) }))} style={compactActionButtonStyle}>
                          <span className="staff-page-button-text" style={compactActionButtonTextStyle}>Down</span>
                        </button>
                        <button
                          type="button"
                          className="staff-page-button"
                          onClick={() => setDocumentData((prev) => ({ ...prev, topics: prev.topics.filter((_, index) => index !== topicIndex) }))}
                          style={{ ...compactActionButtonStyle, background: '#ffd7d7' }}
                          disabled={documentData.topics.length === 1}
                        >
                          <span className="staff-page-button-text" style={compactActionButtonTextStyle}>Remove Topic</span>
                        </button>
                      </div>
                    </div>

                    {!isTopicCollapsed && (
                      <>
                        <div className="form-row" style={{ marginTop: '20px' }}>
                          <div className="form-group">
                            <label className="form-label">Slug</label>
                            <input
                              className="form-input"
                              value={topic.slug || ''}
                              onChange={(e) => updateTopic(topicIndex, { ...topic, slug: e.target.value })}
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Navigation label</label>
                            <input
                              className="form-input"
                              value={topic.label || ''}
                              onChange={(e) => updateTopic(topicIndex, { ...topic, label: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">Topic title</label>
                          <input
                            className="form-input"
                            value={topic.title || ''}
                            onChange={(e) => updateTopic(topicIndex, { ...topic, title: e.target.value })}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Topic intro</label>
                          <textarea
                            className="form-textarea"
                            rows="4"
                            value={topic.intro || ''}
                            onChange={(e) => updateTopic(topicIndex, { ...topic, intro: e.target.value })}
                          />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
                          <h3 style={{ margin: 0, color: '#333' }}>Subtopics</h3>
                          <button
                            type="button"
                            className="staff-page-button"
                            onClick={() => updateTopic(topicIndex, { ...topic, subtopics: [...topic.subtopics, createSubtopic()] })}
                          >
                            <span className="staff-page-button-text">+ Add Subtopic</span>
                          </button>
                        </div>

                        {(topic.subtopics || []).map((subtopic, subtopicIndex) => (
                          <div key={`${subtopic.id}-${subtopicIndex}`} style={{ border: '1px solid #dbe1e8', borderRadius: '12px', padding: '20px', marginTop: '16px', background: '#f8fafc' }}>
                            {(() => {
                              const subtopicKey = `${topicKey}-${subtopic.id || 'subtopic'}-${subtopicIndex}`;
                              const isSubtopicCollapsed = Boolean(collapsedSubtopics[subtopicKey]);

                              return (
                                <>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                    <div>
                                      <h3 style={{ margin: 0, color: '#111827' }}>
                                        Subtopic {subtopicIndex + 1}: {subtopic.label || 'Untitled'}
                                      </h3>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                      <button
                                        type="button"
                                        className="staff-page-button"
                                        onClick={() => toggleSubtopicCollapsed(subtopicKey)}
                                        style={{ ...compactActionButtonStyle, background: '#eef2ff' }}
                                      >
                                        <span className="staff-page-button-text" style={compactActionButtonTextStyle}>{isSubtopicCollapsed ? 'Expand' : 'Collapse'}</span>
                                      </button>
                                      <button
                                        type="button"
                                        className="staff-page-button"
                                        onClick={() => updateTopic(topicIndex, { ...topic, subtopics: moveItem(topic.subtopics, subtopicIndex, -1) })}
                                        style={compactActionButtonStyle}
                                      >
                                        <span className="staff-page-button-text" style={compactActionButtonTextStyle}>Up</span>
                                      </button>
                                      <button
                                        type="button"
                                        className="staff-page-button"
                                        onClick={() => updateTopic(topicIndex, { ...topic, subtopics: moveItem(topic.subtopics, subtopicIndex, 1) })}
                                        style={compactActionButtonStyle}
                                      >
                                        <span className="staff-page-button-text" style={compactActionButtonTextStyle}>Down</span>
                                      </button>
                                      <button
                                        type="button"
                                        className="staff-page-button"
                                        onClick={() => updateTopic(topicIndex, { ...topic, subtopics: topic.subtopics.filter((_, index) => index !== subtopicIndex) })}
                                        style={{ ...compactActionButtonStyle, background: '#ffd7d7' }}
                                      >
                                        <span className="staff-page-button-text" style={compactActionButtonTextStyle}>Remove Subtopic</span>
                                      </button>
                                    </div>
                                  </div>

                                  {!isSubtopicCollapsed && (
                                    <>
                                      <div className="form-row" style={{ marginTop: '16px' }}>
                                        <div className="form-group">
                                          <label className="form-label">Anchor ID</label>
                                          <input
                                            className="form-input"
                                            value={subtopic.id || ''}
                                            onChange={(e) => updateSubtopic(topicIndex, subtopicIndex, { ...subtopic, id: e.target.value })}
                                          />
                                        </div>
                                        <div className="form-group">
                                          <label className="form-label">Subtopic label</label>
                                          <input
                                            className="form-input"
                                            value={subtopic.label || ''}
                                            onChange={(e) => updateSubtopic(topicIndex, subtopicIndex, { ...subtopic, label: e.target.value })}
                                          />
                                        </div>
                                      </div>

                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                                        <h4 style={{ margin: 0, color: '#374151' }}>Blocks</h4>
                                        <button
                                          type="button"
                                          className="staff-page-button"
                                          onClick={() => updateSubtopic(topicIndex, subtopicIndex, { ...subtopic, blocks: [...(subtopic.blocks || []), createBlock()] })}
                                        >
                                          <span className="staff-page-button-text">+ Add Block</span>
                                        </button>
                                      </div>

                                      {(subtopic.blocks || []).map((block, blockIndex) => (
                                        <BlockEditor
                                          key={`${subtopic.id}-${blockIndex}`}
                                          block={block}
                                          onChange={(nextBlock) => updateBlock(topicIndex, subtopicIndex, blockIndex, nextBlock)}
                                          onMoveUp={() => updateSubtopic(topicIndex, subtopicIndex, { ...subtopic, blocks: moveItem(subtopic.blocks || [], blockIndex, -1) })}
                                          onMoveDown={() => updateSubtopic(topicIndex, subtopicIndex, { ...subtopic, blocks: moveItem(subtopic.blocks || [], blockIndex, 1) })}
                                          onRemove={() => updateSubtopic(topicIndex, subtopicIndex, { ...subtopic, blocks: (subtopic.blocks || []).filter((_, index) => index !== blockIndex) })}
                                        />
                                      ))}
                                    </>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        ))}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SuperuserDocsManager;
